import { hasInjectionContext, inject, type InjectionKey, type Ref } from 'vue'
import type { AppLocale } from '@/shared/i18n'

export interface GuildSummary {
  readonly id: number
  readonly name: string
  readonly discordGuildId: string
  readonly ownerDiscordId: string
  readonly officerRoleDiscordId: string | null
  readonly accessRoleDiscordId: string | null
  readonly isOfficer: boolean
  readonly canEditHistoricalRaids: boolean
  readonly raidPublishChannelId: string | null
  readonly raidPublishWindowDays: number
  readonly completedRaidMessageRetentionHours: number
  readonly language: AppLocale | null
}

/**
 * A Discord guild the signed-in user belongs to but cannot currently view.
 *
 * Keep this distinct from `GuildSummary`: callers must never accidentally
 * pass a locked entry into `setActive()` or a guild-scoped data fetch.
 */
export interface LockedGuildSummary {
  readonly id: number
  readonly name: string
  readonly discordGuildId: string
  readonly accessRequested: boolean
}

export interface GuildPort {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly dbGuildId: Readonly<Ref<string | null>>
  readonly availableGuilds: Readonly<Ref<ReadonlyArray<GuildSummary>>>
  readonly lockedGuilds: Readonly<Ref<ReadonlyArray<LockedGuildSummary>>>
  readonly loading: Readonly<Ref<boolean>>
  readonly ownerDiscordIdByGuildId: Readonly<Ref<ReadonlyMap<string, string>>>
  setActive(id: string): Promise<void>
  /** Fail-closed deselection when the current guild is no longer authorized. */
  clearActive(): void
  loadAvailableGuilds(): Promise<void>
  /** Request the configured access role for a locked Discord guild membership. */
  requestGuildAccess(discordGuildId: string): Promise<boolean>
  /** Resolve the DB-internal id for a Discord guild id (or `null` if unknown). */
  resolveDbGuildId(discordGuildId: string): Promise<string | null>
  /** Cascade-event registry for features to subscribe to active-guild changes. */
  onActiveGuildChange(cb: () => void | Promise<void>): () => void
}

export const GUILD_PORT: InjectionKey<GuildPort> = Symbol('GuildPort')

/**
 * Host-installed fallback for callers outside a Vue setup context.
 *
 * Inside setup (component / Pinia store), `inject(GUILD_PORT)` resolves
 * the port from the app's `provide` chain and this fallback is unused.
 *
 * Outside setup — the feature install entry (`installGvgPlanner`),
 * the nav-link descriptor builder (`gvgPlannerNavLinks`), host test
 * harnesses, `node --test` — there is no injection context, so
 * `inject()` returns the default value (`null`) and Vue emits the
 * `[Vue warn] inject() can only be used inside setup()` warning. The
 * host installs its concrete impl here at boot time so non-setup
 * callers reach the same instance the app provides via `provide()`.
 *
 * Cleared on `_resetGuildPortFallback()` so test harnesses can isolate
 * themselves from a previous boot's registration.
 */
let fallbackGuildPort: GuildPort | null = null

/** Host-only: register (or clear with `null`) the non-injection fallback. */
export function _setGuildPortFallback(p: GuildPort | null): void {
  fallbackGuildPort = p
}

/** Test-only: clear the registered fallback. */
export function _resetGuildPortFallback(): void {
  fallbackGuildPort = null
}

export const useGuild = (): GuildPort => {
  // Only attempt `inject()` when we know we're inside a setup context.
  // Without this guard Vue emits `[Vue warn] inject() can only be used
  // inside setup() or functional components.` for every non-setup call,
  // even when there's a default value (the warn fires before the
  // default is consulted, so the default can't suppress it).
  if (hasInjectionContext()) {
    const fromInject = inject(GUILD_PORT, null)
    if (fromInject) return fromInject
  }
  if (fallbackGuildPort) return fallbackGuildPort
  throw new Error('GuildPort not provided')
}
